/**
 * Client-side image compression.
 *
 * Resizes large images down to a max dimension and re-encodes them as WebP
 * (with JPEG fallback) before upload. Reduces Supabase storage size and
 * download bytes by ~70-90% without needing the paid Image Transformations.
 */

export interface CompressOptions {
  /** Max width OR height in px (whichever is larger). Default 1600. */
  maxDimension?: number;
  /** Encoder quality 0-1. Default 0.82. */
  quality?: number;
  /** Output mime type. Default 'image/webp'. */
  mimeType?: 'image/webp' | 'image/jpeg';
}

const SKIP_TYPES = new Set(['image/gif', 'image/svg+xml']);

export async function compressImage(
  file: File,
  opts: CompressOptions = {},
): Promise<File> {
  const { maxDimension = 1600, quality = 0.82, mimeType = 'image/webp' } = opts;

  if (!file.type.startsWith('image/')) return file;
  if (SKIP_TYPES.has(file.type)) return file;
  if (file.size < 100 * 1024) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const { width, height } = bitmap;
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const useOffscreen = typeof OffscreenCanvas !== 'undefined';
  const canvas: HTMLCanvasElement | OffscreenCanvas = useOffscreen
    ? new OffscreenCanvas(targetW, targetH)
    : Object.assign(document.createElement('canvas'), {
        width: targetW,
        height: targetH,
      });

  const ctx = (canvas as HTMLCanvasElement).getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) {
    bitmap.close?.();
    return file;
  }

  ctx.drawImage(bitmap as CanvasImageSource, 0, 0, targetW, targetH);
  bitmap.close?.();

  let blob: Blob | null = null;
  try {
    if ('convertToBlob' in canvas) {
      blob = await (canvas as OffscreenCanvas).convertToBlob({ type: mimeType, quality });
    } else {
      blob = await new Promise<Blob | null>((resolve) =>
        (canvas as HTMLCanvasElement).toBlob(resolve, mimeType, quality),
      );
    }
  } catch {
    blob = null;
  }

  if (!blob || blob.size >= file.size) return file;

  const ext = mimeType === 'image/webp' ? 'webp' : 'jpg';
  const baseName = file.name.replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.${ext}`, {
    type: mimeType,
    lastModified: Date.now(),
  });
}
